<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

import CompanyAPI from 'dashboard/api/companies';
import CrmAPI from 'dashboard/api/crm';
import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CrmMultiSelect from 'dashboard/routes/dashboard/crm/components/CrmMultiSelect.vue';

const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const companyDialog = ref(null);
const editingCompany = ref(null);
const contactRecords = ref([]);
const isLoadingCompany = ref(false);
const isLoadingContacts = ref(false);
const isSavingCompany = ref(false);
const companyForm = reactive({
  name: '',
  domain: '',
  description: '',
  contactIds: [],
});
const pageNumber = computed(() => Number(route.query?.page) || 1);

const activeSort = computed(() => {
  const sortParam = route.query?.sort || 'name';
  return sortParam.startsWith('-') ? sortParam.slice(1) : sortParam;
});

const activeOrdering = computed(() => {
  const sortParam = route.query?.sort || 'name';
  return sortParam.startsWith('-') ? '-' : '';
});

const companies = useMapGetter('companies/getCompaniesList');
const meta = useMapGetter('companies/getMeta');
const uiFlags = useMapGetter('companies/getUIFlags');

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const canSaveCompany = computed(
  () => companyForm.name.trim() && !isLoadingCompany.value
);
const contactOptions = computed(() =>
  contactRecords.value.map(contact => ({
    value: contact.id,
    label: [
      contact.name,
      contact.phone_number || contact.email,
      contact.company?.name,
    ]
      .filter(Boolean)
      .join(' · '),
  }))
);

const sortParam = computed(() => {
  return activeOrdering.value === '-'
    ? `-${activeSort.value}`
    : activeSort.value;
});

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await store.dispatch('companies/search', {
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await store.dispatch('companies/get', {
      page: currentPage,
      sort: currentSort,
    });
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const handleSort = ({ sort, order }) => {
  const newSortParam = order === '-' ? `-${sort}` : sort;
  fetchCompanies(1, searchValue.value, newSortParam);
};

const resetCompanyForm = () => {
  editingCompany.value = null;
  contactRecords.value = [];
  Object.assign(companyForm, {
    name: '',
    domain: '',
    description: '',
    contactIds: [],
  });
};

const mergeContacts = contacts => {
  const records = [...contactRecords.value, ...contacts];
  contactRecords.value = [
    ...new Map(records.map(contact => [contact.id, contact])).values(),
  ];
};

const fetchContactOptions = async (selectedContacts = [], query = '') => {
  mergeContacts(selectedContacts);
  isLoadingContacts.value = true;
  try {
    const response = await CrmAPI.getCommercialContacts({
      q: query.trim() || undefined,
    });
    mergeContacts(response.data.payload || []);
  } catch {
    useAlert(t('COMPANIES.FORM.CONTACTS_LOAD_ERROR'));
  } finally {
    isLoadingContacts.value = false;
  }
};

const searchContacts = debounce(query => {
  fetchContactOptions([], query);
}, DEBOUNCE_DELAY);

const openCreateCompany = () => {
  resetCompanyForm();
  companyDialog.value?.open();
  fetchContactOptions();
};

const openEditCompany = async companyId => {
  resetCompanyForm();
  isLoadingCompany.value = true;
  companyDialog.value?.open();

  try {
    const response = await CompanyAPI.show(companyId);
    const company = response.data.payload;
    const contacts = company.contacts || [];
    editingCompany.value = company;
    Object.assign(companyForm, {
      name: company.name || '',
      domain: company.domain || '',
      description: company.description || '',
      contactIds: contacts.map(contact => contact.id),
    });
    await fetchContactOptions(contacts);
  } catch {
    companyDialog.value?.close();
    useAlert(t('COMPANIES.FORM.LOAD_ERROR'));
  } finally {
    isLoadingCompany.value = false;
  }
};

const normalizedDomain = () =>
  companyForm.domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0];

const saveCompany = async () => {
  if (!canSaveCompany.value) return;

  isSavingCompany.value = true;
  const payload = {
    company: {
      name: companyForm.name.trim(),
      domain: normalizedDomain() || null,
      description: companyForm.description.trim(),
      contact_ids: companyForm.contactIds,
    },
  };

  try {
    const isEditing = Boolean(editingCompany.value);
    if (editingCompany.value) {
      await CompanyAPI.update(editingCompany.value.id, payload);
    } else {
      await CompanyAPI.create(payload);
    }
    companyDialog.value?.close();
    await fetchCompanies(pageNumber.value, searchValue.value, sortParam.value);
    useAlert(
      isEditing ? t('COMPANIES.FORM.UPDATED') : t('COMPANIES.FORM.CREATED')
    );
  } catch {
    useAlert(t('COMPANIES.FORM.SAVE_ERROR'));
  } finally {
    isSavingCompany.value = false;
  }
};

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :create-button-label="t('COMPANIES.CREATE_BUTTON')"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create-company="openCreateCompany"
  >
    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.LOADING')
      }}</span>
    </div>
    <div
      v-else-if="companies.length === 0"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.EMPTY_STATE.TITLE')
      }}</span>
    </div>
    <div v-else class="flex flex-col gap-4 p-4">
      <CompaniesCard
        v-for="company in companies"
        :id="company.id"
        :key="company.id"
        :name="company.name"
        :domain="company.domain"
        :contacts-count="company.contactsCount || 0"
        :description="company.description"
        :avatar-url="company.avatarUrl"
        :updated-at="company.updatedAt"
        @show-company="openEditCompany"
      />
    </div>
  </CompaniesListLayout>

  <Dialog
    ref="companyDialog"
    width="2xl"
    overflow-y-auto
    :title="
      editingCompany
        ? t('COMPANIES.FORM.EDIT_TITLE')
        : t('COMPANIES.FORM.CREATE_TITLE')
    "
    :confirm-button-label="t('COMPANIES.FORM.SAVE')"
    :disable-confirm-button="!canSaveCompany"
    :is-loading="isSavingCompany"
    @confirm="saveCompany"
  >
    <div v-if="isLoadingCompany" class="flex justify-center py-10">
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-10" />
    </div>
    <div v-else class="grid gap-5">
      <label class="grid gap-1.5 text-sm text-n-slate-11">
        <span>{{ t('COMPANIES.FORM.NAME') }}</span>
        <input
          v-model="companyForm.name"
          class="no-margin h-10 rounded-xl border border-n-strong bg-n-solid-3 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          required
        />
      </label>

      <label class="grid gap-1.5 text-sm text-n-slate-11">
        <span>{{ t('COMPANIES.FORM.DOMAIN') }}</span>
        <input
          v-model="companyForm.domain"
          class="no-margin h-10 rounded-xl border border-n-strong bg-n-solid-3 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('COMPANIES.FORM.DOMAIN_PLACEHOLDER')"
        />
      </label>

      <label class="grid gap-1.5 text-sm text-n-slate-11">
        <span>{{ t('COMPANIES.FORM.DESCRIPTION') }}</span>
        <textarea
          v-model="companyForm.description"
          rows="3"
          class="no-margin resize-none rounded-xl border border-n-strong bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('COMPANIES.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </label>

      <label class="grid gap-1.5 text-sm text-n-slate-11">
        <span>{{ t('COMPANIES.FORM.CONTACTS') }}</span>
        <CrmMultiSelect
          v-model="companyForm.contactIds"
          :options="contactOptions"
          :placeholder="t('COMPANIES.FORM.CONTACTS_PLACEHOLDER')"
          :search-placeholder="t('COMPANIES.FORM.CONTACTS_SEARCH')"
          :empty-message="t('COMPANIES.FORM.CONTACTS_EMPTY')"
          @search="searchContacts"
        />
        <span v-if="isLoadingContacts" class="text-xs text-n-slate-9">
          {{ t('COMPANIES.FORM.CONTACTS_LOADING') }}
        </span>
        <span class="text-xs text-n-slate-9">
          {{ t('COMPANIES.FORM.CONTACTS_HELP') }}
        </span>
      </label>
    </div>
  </Dialog>
</template>
